import { Fragment, ReactElement, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft, CircleCheckBig, TriangleAlert, X } from 'lucide-react';
import { useDebouncedCallback } from 'use-debounce';

import { paths } from 'configuration/paths';
import { TranslationsKeys } from 'types/translation';
import { Status, SurveyResponse } from 'types/surveyResponse';
import { useTranslate } from 'hooks/useTranslate';
import { useSurveyTranslation } from 'hooks/useSurveyTranslation';
import { useOnlineStatus } from 'hooks/useOnlineStatus';
import { useSurveyDefinitionQuery, useSurveyResponseQuery } from 'hooks/queries';
import { useDeleteSurveyResponse, useUpdateSurveyResponse } from 'hooks/mutations';
import { MessageBar } from 'components/MessageBar';
import { SurveyHeader } from 'components/SurveyHeader';
import { Footer } from 'components/Footer';
import { Button } from 'components/ui/Button';
import { TextInput } from 'components/ui/TextInput';
import { InProgress } from 'components/ui/CustomIcons/InProgress';
import { SurveyStepper, SurveyStepperSteps } from 'components/SurveyStepper';
import { classNameList } from 'utils/property';
import { getSubsection, isVisible, sortSurvey } from 'utils/survey';
import styles from './SurveyWrapUp.module.scss';

const SurveyWrapUp = (): ReactElement => {
  const { surveyId } = useParams();
  const { t } = useTranslate();
  const surveyResponseQuery = useSurveyResponseQuery(surveyId);
  const surveyResponse = surveyResponseQuery.data;
  const surveyDefinitionQuery = useSurveyDefinitionQuery(
    surveyResponse ? surveyResponse.surveyDefinitionId : undefined
  );
  const surveyDefinition = useMemo(
    () => (surveyDefinitionQuery.data ? sortSurvey(surveyDefinitionQuery.data) : undefined),
    [surveyDefinitionQuery.data]
  );
  const ts = useSurveyTranslation(surveyDefinition);
  const navigate = useNavigate();
  const updateSurveyResponseMutation = useUpdateSurveyResponse();
  const deleteSurveyResponseMutation = useDeleteSurveyResponse();
  const isLoading =
    surveyResponseQuery.isLoading ||
    surveyDefinitionQuery.isLoading ||
    updateSurveyResponseMutation.isPending ||
    deleteSurveyResponseMutation.isPending;
  const isError =
    surveyResponseQuery.isError ||
    surveyDefinitionQuery.isError ||
    updateSurveyResponseMutation.isError ||
    deleteSurveyResponseMutation.isError;
  const [comment, setComment] = useState('');
  const online = useOnlineStatus();

  const counts = useMemo(() => {
    const counts: { [section: string]: { [status in Status | 'all']: string[] } } = {};
    if (surveyDefinition && surveyResponse) {
      for (const section of surveyDefinition.sections) {
        if (!isVisible(section, surveyResponse)) {
          continue;
        }
        
        counts[section.name] = {
          [Status.NotStarted]: [],
          [Status.InProgress]: [],
          [Status.Complete]: [],
          [Status.Skipped]: [],
          all: []
        };
        for (const subsection of section.subsections) {
          if (!isVisible(subsection, surveyResponse)) {
            continue;
          }
          
          const responseSubsection = getSubsection<SurveyResponse>(
            surveyResponse,
            section.sectionId,
            subsection.subsectionId
          );
          if (responseSubsection) {
            counts[section.name][responseSubsection.status].push(subsection.name);
          } else {
            counts[section.name][Status.NotStarted].push(subsection.name);
          }
          counts[section.name]['all'].push(subsection.name);
        }
      }
    }
    return counts;
  }, [surveyDefinition, surveyResponse]);

  const updateCommentDebounced = useDebouncedCallback(
    async (comment: string) => {
      try {
        await updateSurveyResponseMutation.mutateAsync({ ...surveyResponse!, comment });
      } catch (error) {
        console.error('Error updating comment:', error);
      }
    },
    1500,
    { maxWait: 10000 }
  );

  const deleteSurvey = async () => {
    if (!surveyResponse) {
      return;
    }
    try {
      await deleteSurveyResponseMutation.mutateAsync(surveyResponse);
      navigate(paths.mySurveys);
    } catch (error) {
      console.error('Error deleting survey response:', error);
    }
  };

  const submitSurvey = async (complete: boolean) => {
    if (!surveyResponse) {
      return; // shouldn't happen
    }

    let updatedResponse = surveyResponse;
    if (comment !== surveyResponse.comment) {
      updateCommentDebounced.cancel();
      updatedResponse = { ...updatedResponse, comment };
    }
    if (complete && surveyResponse.status !== Status.Complete) {
      updatedResponse = {
        ...updatedResponse,
        status: Status.Complete,
        statusHistory: [
          ...updatedResponse.statusHistory,
          {
            status: Status.Complete,
            changedAt: new Date().toISOString()
          }
        ]
      };
    }

    if (updatedResponse !== surveyResponse) {
      await updateSurveyResponseMutation.mutateAsync(updatedResponse);
    }

    if (complete) {
      navigate(paths.mySurveys_completed + '?submitted=1');
    } else {
      navigate(paths.mySurveys_inProgress);
    }
  };

  return (
    <>
      <SurveyHeader
        isLoading={isLoading}
        isError={isError}
        surveyDefinition={surveyDefinition}
        surveyResponse={surveyResponse ?? undefined}
        onDelete={deleteSurvey}
      />

      <div className={styles.surveyIntro}>
        <SurveyStepper activeStep={SurveyStepperSteps.WrapUp} surveyResponse={surveyResponse} />
      </div>

      <div className={styles.surveyWrapUp}>
        {surveyResponse && !surveyResponse.customer && (
          <div className={styles.surveyWrapUpMessageBar}>
            <MessageBar
              variant="warning"
              title={t(TranslationsKeys.surveyWrapUpPage_customerRequiredTitle)}
              showCloseAction={false}
              description={t(TranslationsKeys.surveyWrapUpPage_customerRequiredDescription)}>
              <Button size="small" variant="secondary" onClick={() => navigate(paths.surveyIntroExistingSurvey.replace(':surveyId', surveyId!))}>
                <ArrowLeft /> {t(TranslationsKeys.surveyWrapUpPage_customerRequiredButton)}
              </Button>
            </MessageBar>
          </div>
        )}
        
        {!online.online && (
          <div className={styles.surveyWrapUpMessageBar}>
            <MessageBar
              variant="warning"
              title=""
              showCloseAction={false}
              description={t(TranslationsKeys.surveyWrapUpPage_offlineWarning)}
            />
          </div>
        )}

        <Button size="small" variant="secondary" onClick={() => navigate(paths.survey.replace(':surveyId', surveyId!))}>
          <ArrowLeft /> {t(TranslationsKeys.surveyWrapUpPage_editSurvey)}
        </Button>

        {Object.entries(counts).map(([section, subsectionCounts]) => (
          <div className={styles.surveyWrapUpSection} key={section}>
            <h1>{ts(section)}</h1>
            <dl>
              {Object.entries(subsectionCounts).map(([status, subsections]) =>
                status !== 'all' && subsections.length ? (
                  <Fragment key={status}>
                    <dt>
                      {status === Status.NotStarted && (
                        <>
                          <span className={classNameList(styles.surveyWrapUpIcon, styles.danger)}>
                            <TriangleAlert />
                          </span>
                          {t(TranslationsKeys.common_surveyStatus_notStarted)}
                        </>
                      )}
                      {status === Status.InProgress && (
                        <>
                          <span className={classNameList(styles.surveyWrapUpIcon, styles.warning)}>
                            <InProgress />
                          </span>
                          {t(TranslationsKeys.common_surveyStatus_inProgress)}
                        </>
                      )}
                      {status === Status.Complete && (
                        <>
                          <span className={classNameList(styles.surveyWrapUpIcon, styles.success)}>
                            <CircleCheckBig />
                          </span>
                          {t(TranslationsKeys.common_surveyStatus_completed)}
                        </>
                      )}
                      {status === Status.Skipped && (
                        <>
                          <span className={classNameList(styles.surveyWrapUpIcon, styles.skipped)}>
                            <X />
                          </span>
                          {t(TranslationsKeys.common_surveyStatus_skipped)}
                        </>
                      )}{' '}
                      {subsections.length}/{subsectionCounts['all'].length}
                    </dt>
                    <dd>{subsections.map((ss) => ts(ss)).join(', ')}</dd>
                  </Fragment>
                ) : null
              )}
            </dl>
          </div>
        ))}

        {surveyResponse && surveyDefinition && (
          <>
            <Button
              size="small"
              variant="secondary"
              onClick={() => navigate(paths.survey.replace(':surveyId', surveyId!))}>
              <ArrowLeft /> {t(TranslationsKeys.surveyWrapUpPage_editSurvey)}
            </Button>

            <div className={styles.surveyWrapUpComment}>
              <TextInput
                label={t(TranslationsKeys.surveyWrapUpPage_customerVisitInsights)}
                subLabel={t(TranslationsKeys.surveyWrapUpPage_customerVisitInsightsDescription)}
                name="comment"
                text={comment}
                onChange={(e) => {
                  setComment(e.target.value);
                  updateCommentDebounced(e.target.value);
                }}
                textarea
              />
            </div>
          </>
        )}
      </div>

      <Footer className={styles.surveyWrapUpFooter}>
        <Button
          variant="secondary"
          state={isLoading || isError ? 'disabled' : 'normal'}
          onClick={() => submitSurvey(false)}>
          {t(TranslationsKeys.surveyWrapUpPage_saveAsDraft)}
        </Button>
        {/* There is no inherent reason why survey cannot be put into completed state while offine, but that is how the UI is designed */}
        <Button
          variant="primary"
          state={isLoading || isError || !online.online || !surveyResponse?.customer ? 'disabled' : 'normal'}
          onClick={() => submitSurvey(true)}>
          {t(TranslationsKeys.surveyWrapUpPage_submit)}
        </Button>
      </Footer>
    </>
  );
};

export default SurveyWrapUp;
